import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LeadStatus,
  LeadStatusValues,
} from '../../../pages/leads/models/lead-status';
import { LeadsService } from '../../../pages/leads/services/leads.service';
import { SearchService } from '../../../pages/search/services/search.service';
import { CommonService } from '../../services/common/common.service';
import { ToastType } from '../../interfaces/common';
import { ClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { PopupComponent } from '../popup/popup.component';

export interface LeadStatusStage {
  step: number;
  value: string;
  name: string;
  sublabel: string;
  isTerminal?: boolean;
}

export interface OtherStatusOption {
  value: string;
  label: string;
  description: string;
  colorClass: 'amber' | 'blue' | 'purple' | 'rose' | 'slate';
  icon: 'pause' | 'info' | 'mail' | 'minus' | 'close';
}

@Component({
  selector: 'app-lead-pipeline-tracker',
  standalone: true,
  imports: [CommonModule, FormsModule, ClickOutsideDirective, PopupComponent],
  templateUrl: './lead-pipeline-tracker.component.html',
  styleUrl: './lead-pipeline-tracker.component.scss',
})
export class LeadPipelineTrackerComponent implements OnInit, OnChanges {
  @Input({ required: true }) recordId!: string;
  @Input() currentStatus?: string | number | null = null;
  @Input() leadAttributes?: Record<string, unknown> | null = null;
  @Input() hasAccess = true;
  @Input() isEditClicked = false;

  @Output() statusChanged = new EventEmitter<{
    uniqueId: string;
    newStatus: string;
    previousStatus?: string;
    approvedAmount?: number | null;
  }>();

  private leadsService = inject(LeadsService);
  private searchService = inject(SearchService);
  private commonService = inject(CommonService);

  isUpdating = false;
  showOtherStatusesDropdown = false;

  // Star Rating (Viability Rating: 1 to 5)
  hoverRating = 0;
  isSavingRating = false;
  readonly ratingStars = [1, 2, 3, 4, 5];
  readonly viabilityLabels: Record<number, string> = {
    1: 'Low viability',
    2: 'Below average viability',
    3: 'Average viability',
    4: 'Above average viability',
    5: 'High viability',
  };

  // Status Notes
  statusNotesInput = '';
  originalStatusNotes = '';
  isSavingNotes = false;
  notesSavedRecently = false;

  // Funded / TA Sub-Selection Dialog
  showFundedDialog = false;
  selectedFundedType: '9' | '10' | '11' = '9';
  approvedAmountInput: string | number = '';
  approvedAmountError = '';

  // Confirmation warning popup
  showWarningDialog = false;
  warningHeading = 'Confirm Status Change';
  warningMessage = '';
  pendingStatusUpdate: {
    status: string;
    approvedAmount?: number | null;
  } | null = null;

  // 3-Stage Linear Happy Path Progression
  readonly statusStages: LeadStatusStage[] = [
    {
      step: 1,
      value: LeadStatusValues['under_review'], // '1'
      name: 'Under Review',
      sublabel: 'Initial review',
    },
    {
      step: 2,
      value: LeadStatusValues['active_client'], // '7'
      name: 'Active Client',
      sublabel: 'Active engagement',
    },
    {
      step: 3,
      value: 'funded_ta', // Represents 9, 10, or 11
      name: 'Funded / TA',
      sublabel: 'Funding / TA completed',
      isTerminal: true,
    },
  ];

  // Holding and Branch Statuses (Including Prospective Client '3')
  readonly otherStatuses: OtherStatusOption[] = [
    {
      value: LeadStatusValues['prospective_client'], // '3'
      label: 'Prospective Client',
      description: 'Intake & qualification in progress',
      colorClass: 'blue',
      icon: 'info',
    },
    {
      value: LeadStatusValues['further_information_required'], // '2'
      label: 'Further Info Required',
      description: 'Awaiting documents or responses',
      colorClass: 'blue',
      icon: 'info',
    },
    {
      value: LeadStatusValues['on_hold'], // '4'
      label: 'On Hold',
      description: 'Paused for now',
      colorClass: 'amber',
      icon: 'pause',
    },
    {
      value: LeadStatusValues['request_lead_contact'], // '8'
      label: 'Request Lead Contact',
      description: 'Outreach & contact requested',
      colorClass: 'purple',
      icon: 'mail',
    },
    {
      value: LeadStatusValues['not_currently_pursuing'], // '6'
      label: 'Not Currently Pursuing',
      description: 'Not the right fit currently',
      colorClass: 'slate',
      icon: 'minus',
    },
    {
      value: LeadStatusValues['declined'], // '5'
      label: 'Declined',
      description: 'Closed outcome',
      colorClass: 'rose',
      icon: 'close',
    },
  ];

  ngOnInit(): void {
    this.syncApprovedAmountFromAttributes();
    this.syncStatusNotesFromAttributes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadAttributes']) {
      this.syncApprovedAmountFromAttributes();
      this.syncStatusNotesFromAttributes();
    }
  }

  private syncApprovedAmountFromAttributes(): void {
    const amount = this.leadAttributes?.['approved_loan_amount'];
    if (typeof amount === 'string' || typeof amount === 'number') {
      this.approvedAmountInput = amount;
    }
  }

  private syncStatusNotesFromAttributes(): void {
    const notes = this.leadAttributes?.['status_notes'];
    this.statusNotesInput = typeof notes === 'string' ? notes : '';
    this.originalStatusNotes = this.statusNotesInput;
  }

  get normalizedCurrentStatus(): string {
    if (
      this.currentStatus === null ||
      this.currentStatus === undefined ||
      this.currentStatus === ''
    ) {
      return '0';
    }
    if (typeof this.currentStatus === 'object') {
      const obj = this.currentStatus as Record<string, unknown>;
      return String(obj?.['value'] ?? obj?.['status'] ?? '0').trim();
    }
    return String(this.currentStatus).trim();
  }

  /** Current active stage step in the 3-step linear flow (1, 2, 3, or 0 if non-linear status) */
  get currentStatusStep(): number {
    const raw = this.normalizedCurrentStatus;
    if (raw === LeadStatusValues['under_review']) return 1;
    if (raw === LeadStatusValues['active_client']) return 2;
    if (
      raw === LeadStatusValues['active_funded'] ||
      raw === LeadStatusValues['active_funded_grant'] ||
      raw === LeadStatusValues['active_technical_assistance']
    ) {
      return 3;
    }
    return 0; // In holding or branch state
  }

  /** Checks if the status is currently one of the 3 funded/TA statuses */
  get isFundedOrTaActive(): boolean {
    const raw = this.normalizedCurrentStatus;
    return (
      raw === LeadStatusValues['active_funded'] ||
      raw === LeadStatusValues['active_funded_grant'] ||
      raw === LeadStatusValues['active_technical_assistance']
    );
  }

  /** True when status is funded loan or grant AND an approved amount is available */
  get isFundedWithAmount(): boolean {
    const raw = this.normalizedCurrentStatus;
    const isFunded =
      raw === LeadStatusValues['active_funded'] ||
      raw === LeadStatusValues['active_funded_grant'];
    const amount = this.leadAttributes?.['approved_loan_amount'];
    return isFunded && (typeof amount === 'number' ? amount > 0 : !!amount);
  }

  /** Returns the approved loan amount formatted as a USD currency string */
  get formattedApprovedAmount(): string {
    const amount = this.leadAttributes?.['approved_loan_amount'];
    if (amount === null || amount === undefined || amount === '') return '';
    const num =
      typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  /** Returns user-friendly status label with exact formatting */
  get currentStatusDisplay(): string {
    const raw = this.normalizedCurrentStatus;
    const map: Record<string, string> = {
      '0': 'Not Set',
      '1': 'Under Review',
      '2': 'Further Info Required',
      '3': 'Prospective Client',
      '4': 'On Hold',
      '5': 'Declined',
      '6': 'Not Pursuing',
      '7': 'Active Client',
      '8': 'Request Lead Contact',
      '9': 'Funded (Loan)',
      '10': 'Funded (Grant)',
      '11': 'Tech Assistance',
    };
    return map[raw] || 'Active Lead';
  }

  get currentStatusColorClass(): string {
    const raw = this.normalizedCurrentStatus;
    if (raw === '1' || raw === '3' || raw === '7') return 'color-blue';
    if (raw === '9' || raw === '10' || raw === '11') return 'color-emerald';
    if (raw === '4') return 'color-amber';
    if (raw === '2' || raw === '8') return 'color-purple';
    if (raw === '5') return 'color-rose';
    if (raw === '6') return 'color-slate';
    return 'color-grey';
  }

  get isHoldingState(): boolean {
    return this.currentStatusStep === 0 && this.normalizedCurrentStatus !== '0';
  }

  get holdingStateBannerText(): string {
    const raw = this.normalizedCurrentStatus;
    switch (raw) {
      case '3':
        return 'Lead is marked as Prospective Client. Advance to Active Client or Funded / TA when ready.';
      case '4':
        return 'Lead is paused On Hold. Click any status milestone below to resume progress.';
      case '2':
        return 'Further info is required. Click a milestone step below once details are gathered.';
      case '8':
        return 'Lead contact requested. Advance to Active Client when outreach is complete.';
      case '6':
        return 'Marked as Not Currently Pursuing. Click any stage to reactivate this lead.';
      case '5':
        return 'Lead was Declined. You can re-engage and move this lead back to active status at any time.';
      default:
        return 'Lead is currently in this status. Advance to any stage above when ready.';
    }
  }

  // --- Star Rating (Viability) Logic ---

  get currentViabilityRating(): number {
    const val = this.leadAttributes?.['viability_rating'];
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  }

  get viabilityTooltip(): string {
    const active = this.hoverRating || this.currentViabilityRating;
    if (active && this.viabilityLabels[active]) {
      return `Viability: ${this.viabilityLabels[active]} (${active}/5)`;
    }
    return 'Click to rate lead viability (1-5 stars)';
  }

  get activeRating(): number {
    return this.hoverRating || this.currentViabilityRating;
  }

  get viabilityRatingColorClass(): string {
    switch (this.activeRating) {
      case 1:
        return 'rating-rose';
      case 2:
        return 'rating-orange';
      case 3:
        return 'rating-amber';
      case 4:
        return 'rating-lime';
      case 5:
        return 'rating-emerald';
      default:
        return 'rating-none';
    }
  }

  get viabilityDisplayText(): string {
    const active = this.activeRating;
    if (active && this.viabilityLabels[active]) {
      return `${this.viabilityLabels[active]} (${active}/5)`;
    }
    return 'Viability Rating';
  }

  setHoverRating(rating: number): void {
    if (!this.hasAccess || this.isSavingRating) return;
    this.hoverRating = rating;
  }

  clearHoverRating(): void {
    this.hoverRating = 0;
  }

  onSelectRating(rating: number): void {
    if (!this.hasAccess || this.isSavingRating || !this.recordId) return;
    if (this.currentViabilityRating === rating) return;

    this.isSavingRating = true;
    this.searchService
      .updateLeadAttributes(this.recordId, { viability_rating: rating })
      .subscribe({
        next: () => {
          this.isSavingRating = false;
          if (this.leadAttributes) {
            this.leadAttributes['viability_rating'] = rating;
          }
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: `Viability set to ${this.viabilityLabels[rating] || rating + ' stars'}!`,
          });
        },
        error: (err) => {
          this.isSavingRating = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: err?.error?.detail || 'Failed to update viability rating.',
          });
        },
      });
  }

  // --- Status Notes Logic ---

  get isNotesDirty(): boolean {
    return this.statusNotesInput.trim() !== this.originalStatusNotes.trim();
  }

  onSaveStatusNotes(): void {
    if (!this.hasAccess || this.isSavingNotes || !this.recordId) return;
    const trimmed = this.statusNotesInput.trim();
    if (trimmed === this.originalStatusNotes.trim()) return;

    this.isSavingNotes = true;
    this.searchService
      .updateLeadAttributes(this.recordId, { status_notes: trimmed })
      .subscribe({
        next: () => {
          this.isSavingNotes = false;
          this.originalStatusNotes = trimmed;
          if (this.leadAttributes) {
            this.leadAttributes['status_notes'] = trimmed;
          }
          this.notesSavedRecently = true;
          setTimeout(() => {
            this.notesSavedRecently = false;
          }, 2500);
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Status notes saved!',
          });
        },
        error: (err) => {
          this.isSavingNotes = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: err?.error?.detail || 'Failed to save status notes.',
          });
        },
      });
  }

  autoResizeTextarea(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  // --- Stage Click & Progression Handlers ---

  onStageClick(stage: LeadStatusStage): void {
    if (!this.hasAccess || this.isUpdating) return;

    // If clicking Step 3 (Funded / TA), open sub-selector dialog
    if (stage.isTerminal || stage.step === 3) {
      const raw = this.normalizedCurrentStatus;
      if (raw === '10') {
        this.selectedFundedType = '10';
      } else if (raw === '11') {
        this.selectedFundedType = '11';
      } else {
        this.selectedFundedType = '9';
      }
      this.approvedAmountError = '';
      this.showFundedDialog = true;
      return;
    }

    const targetStatus = stage.value;
    if (targetStatus === this.normalizedCurrentStatus) return; // Already on this status

    // Check if downgrading from Active Client or Funded status
    if (this.requiresWarningConfirmation(targetStatus)) {
      this.promptWarning(
        targetStatus,
        `Are you sure you want to change the status from ${this.currentStatusDisplay} to ${stage.name}?`,
      );
      return;
    }

    this.executeStatusUpdate(targetStatus);
  }

  onSelectOtherStatus(option: OtherStatusOption): void {
    this.showOtherStatusesDropdown = false;
    if (!this.hasAccess || this.isUpdating) return;
    if (option.value === this.normalizedCurrentStatus) return;

    if (this.requiresWarningConfirmation(option.value)) {
      this.promptWarning(
        option.value,
        `Are you sure you want to change the status from ${this.currentStatusDisplay} to ${option.label}?`,
      );
      return;
    }

    this.executeStatusUpdate(option.value);
  }

  private requiresWarningConfirmation(newStatus: string): boolean {
    const curr = Number(this.normalizedCurrentStatus);
    const next = Number(newStatus);

    // Warning if changing away from Active Client (7) to non-active
    const isCurrentActive = curr === 7;
    const isNextActive = [7, 9, 10, 11].includes(next);
    if (isCurrentActive && !isNextActive) return true;

    // Warning if changing away from Funded/TA (9, 10, 11) to non-funded
    const isCurrentFunded = [9, 10, 11].includes(curr);
    const isNextFunded = [9, 10, 11].includes(next);
    if (isCurrentFunded && !isNextFunded) return true;

    return false;
  }

  private promptWarning(targetStatus: string, message: string): void {
    this.warningHeading = 'Confirm Status Change';
    this.warningMessage = message;
    this.pendingStatusUpdate = { status: targetStatus };
    this.showWarningDialog = true;
  }

  onConfirmWarning(): void {
    if (this.pendingStatusUpdate) {
      const { status, approvedAmount } = this.pendingStatusUpdate;
      this.showWarningDialog = false;
      this.pendingStatusUpdate = null;
      this.executeStatusUpdate(status, approvedAmount);
    }
  }

  onCancelWarning(): void {
    this.showWarningDialog = false;
    this.pendingStatusUpdate = null;
  }

  // --- Funded Dialog Logic ---

  onConfirmFundedDialog(): void {
    const status = this.selectedFundedType;

    // If loan (9) or grant (10), validate approved amount
    if (status === '9' || status === '10') {
      const val = String(this.approvedAmountInput ?? '').trim();
      const num = Number(val);
      if (!val) {
        this.approvedAmountError =
          'Approved amount is required for funded leads.';
        return;
      }
      if (isNaN(num) || num <= 0) {
        this.approvedAmountError =
          'Please enter a valid amount greater than 0.';
        return;
      }
      this.approvedAmountError = '';
      this.showFundedDialog = false;
      this.executeStatusUpdate(status, num);
    } else {
      // Technical Assistance (11) - no amount required
      this.showFundedDialog = false;
      this.executeStatusUpdate(status, null);
    }
  }

  onCancelFundedDialog(): void {
    this.showFundedDialog = false;
    this.approvedAmountError = '';
  }

  // --- API Execution ---

  private executeStatusUpdate(
    newStatus: string,
    approvedAmount?: number | null,
  ): void {
    if (this.isUpdating || !this.recordId) return;
    this.isUpdating = true;

    const previousStatus = this.normalizedCurrentStatus;
    const newStatusNum = Number(newStatus);

    const finalApprovedAmount =
      approvedAmount !== undefined
        ? approvedAmount
        : ![9, 10].includes(newStatusNum)
          ? null
          : undefined;

    const payloadAttributes: Record<string, unknown> = {
      lead_status: newStatusNum,
    };
    if (finalApprovedAmount !== undefined) {
      payloadAttributes['approved_loan_amount'] = finalApprovedAmount;
    }

    // Also include any pending status notes edit
    const currentNotesTrimmed = this.statusNotesInput.trim();
    if (currentNotesTrimmed !== this.originalStatusNotes.trim()) {
      payloadAttributes['status_notes'] = currentNotesTrimmed;
    }

    const leadStatusUpdateItem: LeadStatus = {
      unique_id: this.recordId,
      lead_status: newStatus,
    };
    if (finalApprovedAmount !== undefined) {
      leadStatusUpdateItem.approved_loan_amount = finalApprovedAmount;
    }

    // Call updateLeadStatus for lead status tracking and updateLeadAttributes for record detail consistency
    this.leadsService.updateLeadStatus([leadStatusUpdateItem]).subscribe({
      next: () => {
        this.searchService
          .updateLeadAttributes(this.recordId, payloadAttributes)
          .subscribe({
            next: () => {
              this.isUpdating = false;
              this.currentStatus = newStatus;
              if (this.leadAttributes) {
                this.leadAttributes['lead_status'] = newStatusNum;
                if (finalApprovedAmount !== undefined) {
                  this.leadAttributes['approved_loan_amount'] =
                    finalApprovedAmount;
                }
                if (currentNotesTrimmed !== this.originalStatusNotes.trim()) {
                  this.leadAttributes['status_notes'] = currentNotesTrimmed;
                  this.originalStatusNotes = currentNotesTrimmed;
                }
              }

              this.leadsService.triggerStatusCountRefresh();
              this.statusChanged.emit({
                uniqueId: this.recordId,
                newStatus,
                previousStatus,
                approvedAmount:
                  finalApprovedAmount !== undefined
                    ? finalApprovedAmount
                    : null,
              });

              this.commonService.triggerToast({
                type: ToastType.Success,
                message: `Lead status updated to ${this.currentStatusDisplay}!`,
              });
            },
            error: () => {
              this.isUpdating = false;
              this.commonService.triggerToast({
                type: ToastType.Success,
                message: `Lead status updated to ${this.currentStatusDisplay}!`,
              });
              this.leadsService.triggerStatusCountRefresh();
              this.statusChanged.emit({
                uniqueId: this.recordId,
                newStatus,
                previousStatus,
                approvedAmount:
                  finalApprovedAmount !== undefined
                    ? finalApprovedAmount
                    : null,
              });
            },
          });
      },
      error: (err) => {
        this.isUpdating = false;
        this.commonService.triggerToast({
          type: ToastType.Error,
          message:
            err?.error?.detail ||
            'Failed to update lead status. Please try again.',
        });
      },
    });
  }

  toggleOtherStatuses(): void {
    if (!this.hasAccess) return;
    this.showOtherStatusesDropdown = !this.showOtherStatusesDropdown;
  }

  closeOtherStatuses(): void {
    this.showOtherStatusesDropdown = false;
  }
}
